import type {
  AuthoredCard,
  CardInstanceState,
  CombatRuntimeState,
  HeroDefinition,
  MatchState,
  SeatId,
  SeatState,
  SkillId,
} from "../../../schemas"
import { rngValue } from "../rng"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CardLookup = ReadonlyMap<string, AuthoredCard>
export type HeroLookup = ReadonlyMap<string, HeroDefinition>

export type EngineContext = {
  cards: CardLookup
  heroes: HeroLookup
}

export type CommandError = {
  ok: false
  code: string
  message: string
}

export type CommandSuccess = {
  ok: true
  state: MatchState
}

export type CommandResult = CommandSuccess | CommandError

export function err(code: string, message: string): CommandError {
  return { ok: false, code, message }
}

// ---------------------------------------------------------------------------
// Seat accessors
// ---------------------------------------------------------------------------

export function getSeat(state: MatchState, seatId: SeatId): SeatState {
  return state.seats[seatId === "seat_a" ? 0 : 1]
}

export function getOtherSeat(state: MatchState, seatId: SeatId): SeatState {
  return state.seats[seatId === "seat_a" ? 1 : 0]
}

export function getOtherSeatId(seatId: SeatId): SeatId {
  return seatId === "seat_a" ? "seat_b" : "seat_a"
}

// ---------------------------------------------------------------------------
// Immutable update helpers
// ---------------------------------------------------------------------------

export function updateSeat(
  state: MatchState,
  seatId: SeatId,
  updater: (seat: SeatState) => SeatState,
): MatchState {
  return {
    ...state,
    seats: [
      seatId === "seat_a" ? updater(state.seats[0]) : state.seats[0],
      seatId === "seat_b" ? updater(state.seats[1]) : state.seats[1],
    ],
  }
}

export function updateInstance(
  state: MatchState,
  instanceId: string,
  updater: (inst: CardInstanceState) => CardInstanceState,
): MatchState {
  const inst = state.instances[instanceId]
  if (!inst) return state
  return {
    ...state,
    instances: { ...state.instances, [instanceId]: updater(inst) },
  }
}

export function addInstance(
  state: MatchState,
  instance: CardInstanceState,
): MatchState {
  return {
    ...state,
    instances: { ...state.instances, [instance.instanceId]: instance },
  }
}

export function removeFromZoneArray(
  arr: readonly string[],
  instanceId: string,
): string[] {
  return arr.filter((id) => id !== instanceId)
}

/**
 * Move an instance to a new zone, updating both the instance record and
 * the relevant SeatZones arrays.
 */
export function moveInstance(
  state: MatchState,
  instanceId: string,
  toZone: CardInstanceState["zone"],
  toSeatId: SeatId,
): MatchState {
  const inst = state.instances[instanceId]
  if (!inst) return state

  const fromSeatId = inst.ownerSeatId
  const fromZone = inst.zone

  // Remove from current zone
  let s = updateSeat(state, fromSeatId, (seat) => ({
    ...seat,
    zones: removeFromZoneInSeat(seat.zones, fromZone, instanceId),
  }))

  // Add to destination zone
  s = updateSeat(s, toSeatId, (seat) => ({
    ...seat,
    zones: addToZoneInSeat(seat.zones, toZone, instanceId),
  }))

  // Update instance record
  s = updateInstance(s, instanceId, (inst) => ({
    ...inst,
    zone: toZone,
    ownerSeatId: toSeatId,
    currentHealth: shouldRefreshCardHealth(toZone)
      ? inst.maxHealth
      : inst.currentHealth,
  }))

  return s
}

function shouldRefreshCardHealth(zone: CardInstanceState["zone"]): boolean {
  switch (zone) {
    case "hand":
    case "deck":
    case "discard":
    case "supply":
    case "merc_row":
      return true
    case "active":
    case "support":
    case "scrap":
      return false
  }
}

function removeFromZoneInSeat(
  zones: SeatState["zones"],
  zone: CardInstanceState["zone"],
  instanceId: string,
): SeatState["zones"] {
  switch (zone) {
    case "active":
      return {
        ...zones,
        active: zones.active === instanceId ? null : zones.active,
      }
    case "support":
      return {
        ...zones,
        support: removeFromZoneArray(zones.support, instanceId),
      }
    case "hand":
      return { ...zones, hand: removeFromZoneArray(zones.hand, instanceId) }
    case "deck":
      return {
        ...zones,
        drawDeck: removeFromZoneArray(zones.drawDeck, instanceId),
      }
    case "discard":
      return {
        ...zones,
        discard: removeFromZoneArray(zones.discard, instanceId),
      }
    case "scrap":
      return { ...zones, scrap: removeFromZoneArray(zones.scrap, instanceId) }
    case "supply":
      return { ...zones, supply: removeFromZoneArray(zones.supply, instanceId) }
    case "merc_row":
      return {
        ...zones,
        mercRow: removeFromZoneArray(zones.mercRow, instanceId),
      }
  }
}

function addToZoneInSeat(
  zones: SeatState["zones"],
  zone: CardInstanceState["zone"],
  instanceId: string,
): SeatState["zones"] {
  switch (zone) {
    case "active":
      return { ...zones, active: instanceId }
    case "support":
      return { ...zones, support: [...zones.support, instanceId] }
    case "hand":
      return { ...zones, hand: [...zones.hand, instanceId] }
    case "deck":
      return { ...zones, drawDeck: [...zones.drawDeck, instanceId] }
    case "discard":
      return { ...zones, discard: [...zones.discard, instanceId] }
    case "scrap":
      return { ...zones, scrap: [...zones.scrap, instanceId] }
    case "supply":
      return { ...zones, supply: [...zones.supply, instanceId] }
    case "merc_row":
      return { ...zones, mercRow: [...zones.mercRow, instanceId] }
  }
}

// ---------------------------------------------------------------------------
// Skill and combat helpers
// ---------------------------------------------------------------------------

export function cardHasSkill(
  instance: CardInstanceState,
  skillId: SkillId,
  ctx: EngineContext,
): boolean {
  const def = ctx.cards.get(instance.definitionId)
  if (def) {
    return def.selectedSkills.some((s) => s.skillId === skillId)
  }
  // Hero instance: look up current level's skills
  const hero = ctx.heroes.get(instance.definitionId)
  if (hero) {
    const seat = instance.ownerSeatId // we'd need the seat's hero level — skip for now
    void seat
    // Fall back to checking all levels (conservative — use level 1 only for now)
    return hero.progression[0].card.selectedSkills.some(
      (s) => s.skillId === skillId,
    )
  }
  return false
}

export function seatHasGuard(seatId: SeatId, state: MatchState): boolean {
  const seat = getSeat(state, seatId)
  const zones = [seat.zones.active, ...seat.zones.support]
  return zones.some((id) => {
    if (!id) return false
    const inst = state.instances[id]
    return inst?.combatState.hasGuard === true
  })
}

export function buildCombatState(card: AuthoredCard): CombatRuntimeState {
  const skillIds = new Set(card.selectedSkills.map((s) => s.skillId))
  return {
    ignoresGuard: skillIds.has("sneak"),
    breaksThrough: skillIds.has("breakthrough"),
    hasGuard: skillIds.has("guard"),
    isSwift: skillIds.has("swift"),
    hasArmor: skillIds.has("armor"),
    hasRanged: skillIds.has("ranged"),
    hasCharge: skillIds.has("charge"),
    hasGenerator: skillIds.has("generator"),
    hasFocus: skillIds.has("focus"),
    hasKo: skillIds.has("ko"),
    hasDeploy: skillIds.has("deploy"),
    hasScrap: skillIds.has("scrap"),
    hasSpark: skillIds.has("spark"),
    hasCoordinatedStrike: skillIds.has("coordinated_strike"),
    hasBattery: skillIds.has("battery"),
  }
}

export function buildCombatStateForHeroLevel(
  hero: HeroDefinition,
  level: 1 | 2 | 3,
): CombatRuntimeState {
  const levelDef = hero.progression[level - 1]
  const skillIds = new Set(levelDef.card.selectedSkills.map((s) => s.skillId))
  return {
    ignoresGuard: skillIds.has("sneak"),
    breaksThrough: skillIds.has("breakthrough"),
    hasGuard: skillIds.has("guard"),
    isSwift: skillIds.has("swift"),
    hasArmor: skillIds.has("armor"),
    hasRanged: skillIds.has("ranged"),
    hasCharge: skillIds.has("charge"),
    hasGenerator: skillIds.has("generator"),
    hasFocus: skillIds.has("focus"),
    hasKo: skillIds.has("ko"),
    hasDeploy: skillIds.has("deploy"),
    hasScrap: skillIds.has("scrap"),
    hasSpark: skillIds.has("spark"),
    hasCoordinatedStrike: skillIds.has("coordinated_strike"),
    hasBattery: skillIds.has("battery"),
  }
}

export function countOtherAllies(
  state: MatchState,
  seatId: SeatId,
  sourceId: string,
): number {
  const seat = getSeat(state, seatId)
  return [seat.zones.active, ...seat.zones.support].filter(
    (id): id is string => id !== null && id !== sourceId,
  ).length
}

export function applyTemporaryModifier(
  state: MatchState,
  instanceId: string,
  modifier: {
    attackMod?: number
    healthMod?: number
    expiresAfterTurn: number
    expiresAfterSeatId?: SeatId
  },
): MatchState {
  const attackMod = modifier.attackMod ?? 0
  const healthMod = modifier.healthMod ?? 0

  return updateInstance(state, instanceId, (inst) => ({
    ...inst,
    currentAttack: inst.currentAttack + attackMod,
    currentHealth: inst.currentHealth + healthMod,
    maxHealth: inst.maxHealth + healthMod,
    modifiers: [
      ...inst.modifiers,
      {
        attackMod,
        healthMod,
        expiresAfterTurn: modifier.expiresAfterTurn,
        expiresAfterSeatId: modifier.expiresAfterSeatId,
      },
    ],
  }))
}

// ---------------------------------------------------------------------------
// Damage helpers
// ---------------------------------------------------------------------------

export function applyDamageToInstance(
  state: MatchState,
  instanceId: string,
  damage: number,
): MatchState {
  return updateInstance(state, instanceId, (inst) => {
    const armorReduction = inst.combatState.hasArmor ? 1 : 0
    const netDamage = Math.max(0, damage - armorReduction)
    return { ...inst, currentHealth: inst.currentHealth - netDamage }
  })
}

export function applyDamageToCC(
  state: MatchState,
  seatId: SeatId,
  damage: number,
): MatchState {
  return updateSeat(state, seatId, (seat) => ({
    ...seat,
    centralCommand: {
      ...seat.centralCommand,
      currentHealth: Math.max(0, seat.centralCommand.currentHealth - damage),
    },
  }))
}

// ---------------------------------------------------------------------------
// Win condition
// ---------------------------------------------------------------------------

export function checkWinCondition(state: MatchState): MatchState {
  if (state.status === "completed") return state

  for (const seat of state.seats) {
    if (seat.centralCommand.currentHealth <= 0) {
      const winnerId = getOtherSeatId(seat.seatId)
      return {
        ...state,
        status: "completed",
        result: {
          winnerSeat: winnerId,
          loserSeat: seat.seatId,
          reason: "central_command_zero",
          finalTurn: state.turn.number,
        },
      }
    }
  }
  return state
}

// ---------------------------------------------------------------------------
// State-based cleanup
// ---------------------------------------------------------------------------

/** Move any lethally damaged units to their owner's discard pile. */
export function resolveLethalDamage(state: MatchState): MatchState {
  let s = state
  for (const [instanceId, inst] of Object.entries(s.instances)) {
    if (
      inst.currentHealth <= 0 &&
      inst.zone !== "discard" &&
      inst.zone !== "scrap"
    ) {
      // Heroes go to discard; others go to discard too (scrap only via Scrap skill)
      s = moveInstance(s, instanceId, "discard", inst.ownerSeatId)
      // Update memory: KO count on the attacker is handled at the attack site
    }
  }
  return s
}

// ---------------------------------------------------------------------------
// Log helpers
// ---------------------------------------------------------------------------

export function appendLog(
  state: MatchState,
  seatId: SeatId,
  eventType: string,
  payload: Record<string, unknown>,
): MatchState {
  const entry = {
    sequence: state.log.length,
    turn: state.turn.number,
    seatId,
    eventType,
    payload,
  }
  return { ...state, log: [...state.log, entry] }
}

// ---------------------------------------------------------------------------
// Draw helpers
// ---------------------------------------------------------------------------

/**
 * Draw up to `count` cards from the draw deck to hand.
 * Reshuffles discard into deck if needed (consumes RNG calls).
 */
export function drawCards(
  state: MatchState,
  seatId: SeatId,
  count: number,
): MatchState {
  let s = state
  let drawn = 0

  while (drawn < count) {
    const seat = getSeat(s, seatId)

    if (seat.zones.drawDeck.length === 0) {
      if (seat.zones.discard.length === 0) break // nothing left to draw
      // Reshuffle discard into deck
      s = reshuffleDiscard(s, seatId)
      continue
    }

    const [topId, ...rest] = getSeat(s, seatId).zones.drawDeck
    s = updateSeat(s, seatId, (seat) => ({
      ...seat,
      zones: { ...seat.zones, drawDeck: rest },
    }))
    s = moveInstance(s, topId, "hand", seatId)
    drawn++
  }

  return s
}

function reshuffleDiscard(state: MatchState, seatId: SeatId): MatchState {
  const seat = getSeat(state, seatId)
  const discardIds = [...seat.zones.discard]

  // Shuffle discard using seeded RNG
  const seed = state.config.randomSeed
  let rngIndex = state.rngCallIndex
  for (let i = discardIds.length - 1; i > 0; i--) {
    const j = Math.floor(rngValue(seed, rngIndex++) * (i + 1))
    ;[discardIds[i], discardIds[j]] = [discardIds[j], discardIds[i]]
  }

  let s: MatchState = { ...state, rngCallIndex: rngIndex }
  s = updateSeat(s, seatId, (seat) => ({
    ...seat,
    zones: { ...seat.zones, drawDeck: discardIds, discard: [] },
  }))

  // Update zone field on each instance
  for (const id of discardIds) {
    s = updateInstance(s, id, (inst) => ({ ...inst, zone: "deck" }))
  }

  return s
}

// ---------------------------------------------------------------------------
// Supply refill helpers
// ---------------------------------------------------------------------------

/** Refill a merc row slot from the owning seat's supply deck. */
export function refillMercRow(
  state: MatchState,
  sourceSeatId: SeatId,
): MatchState {
  const seat = getSeat(state, sourceSeatId)
  if (seat.zones.supply.length === 0) return state
  if (seat.zones.mercRow.length >= 3) return state // already full

  const [topId, ...rest] = seat.zones.supply

  let s = updateSeat(state, sourceSeatId, (seat) => ({
    ...seat,
    zones: { ...seat.zones, supply: rest },
  }))
  s = moveInstance(s, topId, "merc_row", sourceSeatId)
  return s
}
